import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Shield, Lock, Eye, Server, FileCheck, ChevronRight, ShieldCheck } from "lucide-react";

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://www.machinerymarketed.com/app-logo.png" 
            alt="MachineryMarketed Logo" 
            className="h-10 w-10"
          />
          <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <Link to="/" className="btn-outline text-sm">Back to Home</Link>
      </div>
    </nav>
  );
};

// Security Feature Card
const SecurityCard = ({ icon: Icon, title, description, color = "var(--primary-300)" }) => (
  <div 
    className="card p-8 h-full"
    style={{ background: "var(--background-200)", borderColor: "rgba(176, 176, 176, 0.15)" }}
  >
    <div 
      className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
      style={{ background: `${color}20` }}
    >
      <Icon size={32} style={{ color: color }} />
    </div>
    <h3 
      className="text-xl font-bold mb-4"
      style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
    >
      {title}
    </h3>
    <p style={{ color: "var(--text-200)", lineHeight: "1.7" }}>{description}</p>
  </div>
);

// Main Security Page Component
const SecurityPage = () => {
  const securityFeatures = [
    {
      icon: Lock,
      title: "Industry-Standard Encryption",
      description: "All data is encrypted both in transit and at rest using modern encryption standards. Your information is protected whether it's being uploaded from a phone in the yard or reviewed in the office.",
      color: "#0f5fff"
    },
    {
      icon: Eye,
      title: "Role-Based Access Control",
      description: "Users only see what they're authorised to see, based on their role within the dealership. Salespeople, managers, and principals each have clear, defined permissions, reducing risk and preventing accidental exposure.",
      color: "#0049db"
    },
    {
      icon: Server,
      title: "Enterprise-Grade Infrastructure",
      description: "We follow best-practice cloud infrastructure standards, including hardened servers, continuous monitoring, regular updates, and automatic backups. This protects against data loss, system failure, and unauthorised access.",
      color: "#0038a8"
    },
    {
      icon: FileCheck,
      title: "You Own Your Data",
      description: "MachineryMarketed does not sell, share, or repurpose dealership information — ever. Your customer details, pricing decisions, stock positions, and photos remain yours and yours alone.",
      color: "#0f5fff"
    }
  ];

  return (
    <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
      <Navigation />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="py-20 relative overflow-hidden">
          <div 
            className="absolute inset-0 z-0"
            style={{
              background: "radial-gradient(ellipse at center, rgba(15, 95, 255, 0.1) 0%, transparent 70%)"
            }}
          />
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
                style={{ background: "var(--primary-100)" }}
              >
                <ShieldCheck size={20} style={{ color: "white" }} />
                <span className="text-sm font-semibold" style={{ color: "white" }}>Your Data is Safe</span>
              </div>
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-8 px-2"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)", wordWrap: "break-word" }}
              >
                Security & <span className="text-gradient">Data Protection</span>
              </h1>
              <p 
                className="text-xl max-w-3xl mx-auto"
                style={{ color: "var(--text-200)" }}
              >
                Security isn't an afterthought at MachineryMarketed —{" "}
                <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>it's built in from day one</span>.
              </p>
            </div>
          </div>
        </section>

        {/* Opening Statement */}
        <section className="py-12">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div 
                className="card p-10"
                style={{ 
                  background: "var(--background-200)", 
                  borderColor: "rgba(15, 95, 255, 0.2)",
                  borderLeftWidth: "4px",
                  borderLeftColor: "var(--primary-300)"
                }}
              >
                <p 
                  className="text-lg"
                  style={{ color: "var(--text-200)", lineHeight: "1.8" }}
                >
                  We understand that dealership data is valuable. It includes customer details, 
                  pricing decisions, stock positions, photos, and internal processes. That's why 
                  we've designed MachineryMarketed using the same security principles trusted 
                  across modern finance, enterprise software, and cloud platforms.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Security Features Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {securityFeatures.map((feature, index) => (
                <SecurityCard key={index} {...feature} />
              ))}
            </div>
          </div>
        </section>

        {/* Trust Statement */}
        <section className="py-20" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <Shield size={64} style={{ color: "var(--primary-300)", margin: "0 auto 24px" }} />
              <h2 
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                Our Commitment to You
              </h2>
              <p 
                className="text-xl mb-8"
                style={{ color: "var(--text-200)", lineHeight: "1.7" }}
              >
                We've designed the platform to be at least as secure as the systems dealerships 
                rely on every day, and in many cases, <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>more so</span>.
              </p>
              <p style={{ color: "var(--text-300)" }}>
                Your trust is our foundation. We take the protection of your data seriously.
              </p>
            </div>
          </div>
        </section>

        {/* Legal Links Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div 
              className="card p-12 max-w-4xl mx-auto text-center"
              style={{ 
                background: "linear-gradient(135deg, var(--background-200), var(--background-300))",
                borderColor: "rgba(15, 95, 255, 0.3)"
              }}
            >
              <h3 
                className="text-2xl font-bold mb-4"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                For Full Details
              </h3>
              <p className="mb-8" style={{ color: "var(--text-200)" }}>
                Review our comprehensive legal documents for complete information on data handling, 
                privacy practices, and terms of service.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/privacy" className="btn-outline">
                  Privacy Policy
                </Link>
                <Link to="/terms" className="btn-outline">
                  Terms of Use
                </Link>
                <Link to="/#contact" className="btn-primary inline-flex items-center gap-2">
                  Contact Us <ChevronRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ background: "var(--background-200)", borderTop: "1px solid rgba(176, 176, 176, 0.1)" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://www.machinerymarketed.com/app-logo.png" 
                alt="MachineryMarketed Logo" 
                className="h-10 w-10"
              />
              <span className="font-bold text-lg" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                MACHINERYMARKETED
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Link to="/about" className="footer-link text-sm">About</Link>
              <Link to="/privacy" className="footer-link text-sm">Privacy</Link>
              <Link to="/terms" className="footer-link text-sm">Terms</Link>
              <Link to="/cookies" className="footer-link text-sm">Cookies</Link>
              <Link to="/faq" className="footer-link text-sm">FAQ</Link>
            </div>

            <p className="text-sm" style={{ color: "var(--text-300)" }}>
              © 2026 MachineryMarketed. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SecurityPage;
